import { useEffect, useState } from "react";
import { api } from "./api";

const BASE_TITLE = "TOVRA";

/**
 * Reflect the current user's total unread count in the document title so
 * background tabs still surface pending work (`TOVRA · 3 unread`).
 *
 * Source of truth: `GET /unread-summary` returns
 *   { channels_total, direct_messages_total, notifications_total, total }.
 * The backend also broadcasts `unread.summary` (per-user) whenever a message
 * arrives or a read receipt clears something, and channel/DM read events.
 * We refresh on all of those.
 */
export default function useDocumentTitle({ subscribe, currentOrgId, userId }) {
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!currentOrgId) return;
    let alive = true;

    const refresh = async () => {
      try {
        const { data } = await api.get("/unread-summary");
        if (alive) setUnread(data?.total || 0);
      } catch { /* offline / auth flake — ignore */ }
    };
    refresh();

    if (!subscribe) return () => { alive = false; };
    const unsub = subscribe((type, payload) => {
      if (type === "unread.summary" && payload?.summary) {
        // Filter by user — broadcasts are org-wide.
        if (!payload.user_id || payload.user_id === userId) {
          setUnread(payload.summary.total || 0);
        }
        return;
      }
      if (
        type === "message.new" || type === "message.deleted" ||
        type === "dm.new" || type === "channel.read" ||
        type === "conversation.read"
      ) {
        refresh();
      }
    });
    return () => { alive = false; unsub && unsub(); };
  }, [currentOrgId, userId, subscribe]);

  useEffect(() => {
    const prev = document.title;
    document.title = unread > 0 ? `${BASE_TITLE} · ${unread} unread` : BASE_TITLE;
    return () => { document.title = prev; };
  }, [unread]);

  return unread;
}
